/**
 * Format Validators
 * 
 * Comprehensive validation utilities for all format patterns, providing
 * consistent validation logic across the formatting system.
 */

import { ValidationResult, ValidationRule } from '../types/formatting-types';
import { FieldType } from '../detection-patterns';
import { PhoneNumberUtil } from 'google-libphonenumber';

/**
 * Validation context for advanced validation scenarios
 */
export interface ValidationContext {
  /** Field type being validated */
  fieldType: FieldType;
  /** Current field value */
  value: string;
  /** Raw unformatted value */
  rawValue: string;
  /** Field name for context-specific validation */
  fieldName?: string;
  /** Additional context data */
  context?: Record<string, any>;
  /** Whether this is real-time validation */
  isRealTime?: boolean;
}

/**
 * Validation rule processor function
 */
export type ValidationRuleProcessor = (
  value: string,
  params?: Record<string, any>,
  context?: ValidationContext
) => ValidationResult;

/**
 * Built-in validation rules registry
 */
export class ValidationRuleRegistry {
  private rules = new Map<string, ValidationRuleProcessor>();

  constructor() {
    this.registerBuiltInRules();
  }

  /**
   * Register a validation rule
   */
  public registerRule(name: string, processor: ValidationRuleProcessor): void {
    this.rules.set(name, processor);
  }

  /**
   * Get validation rule processor
   */
  public getRule(name: string): ValidationRuleProcessor | undefined {
    return this.rules.get(name);
  }

  /**
   * Process a validation rule
   */
  public processRule(
    ruleName: string,
    value: string,
    params?: Record<string, any>,
    context?: ValidationContext
  ): ValidationResult {
    const processor = this.rules.get(ruleName);
    if (!processor) {
      return {
        isValid: false,
        errors: [`Unknown validation rule: ${ruleName}`],
        warnings: []
      };
    }

    try {
      return processor(value, params, context);
    } catch (error) {
      return {
        isValid: false,
        errors: [`Validation rule error: ${error}`],
        warnings: []
      };
    }
  }

  /**
   * Register all built-in validation rules
   */
  private registerBuiltInRules(): void {
    // Required field validation
    this.registerRule('required', (value) => ({
      isValid: value.trim().length > 0,
      errors: value.trim().length === 0 ? ['This field is required'] : [],
      warnings: []
    }));

    // Length validation
    this.registerRule('length', (value, params) => {
      const errors: string[] = [];
      
      if (params?.min && value.length < params.min) {
        errors.push(`Minimum length is ${params.min} characters`);
      }
      
      if (params?.max && value.length > params.max) {
        errors.push(`Maximum length is ${params.max} characters`);
      }
      
      if (params?.exact && value.length !== params.exact) {
        errors.push(`Must be exactly ${params.exact} characters`);
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings: []
      };
    });

    // Pattern validation (regex)
    this.registerRule('pattern', (value, params) => {
      if (!params?.pattern) {
        return {
          isValid: false,
          errors: ['Pattern validation requires a pattern parameter'],
          warnings: []
        };
      }

      const regex = new RegExp(params.pattern);
      const isValid = regex.test(value);

      return {
        isValid,
        errors: isValid ? [] : [params.message || 'Value does not match required pattern'],
        warnings: []
      };
    });

    // Numeric validation
    this.registerRule('numeric', (value, params) => {
      const numValue = parseFloat(value);
      const errors: string[] = [];
      
      if (isNaN(numValue)) {
        errors.push('Must be a valid number');
        return { isValid: false, errors, warnings: [] };
      }

      if (params?.min !== undefined && numValue < params.min) {
        errors.push(`Must be at least ${params.min}`);
      }
      
      if (params?.max !== undefined && numValue > params.max) {
        errors.push(`Must be no more than ${params.max}`);
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings: []
      };
    });

    // Date validation
    this.registerRule('date', (value, params, context) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      // Progressive validation for partial dates
      const parts = value.split('/');
      
      // If not in full format, do progressive validation
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        // Check what we have so far
        if (parts.length === 1 && parts[0].length === 2) {
          // Just MM entered
          const month = parseInt(parts[0]);
          if (month < 1 || month > 12) {
            errors.push('*Month isn\'t right');
          }
        } else if (parts.length === 2 && parts[0].length === 2 && parts[1].length === 2) {
          // MM/DD entered
          const month = parseInt(parts[0]);
          const day = parseInt(parts[1]);
          
          if (month < 1 || month > 12) {
            errors.push('*Month isn\'t right');
          }
          if (day < 1 || day > 31) {
            errors.push('*Day must be between 1 and 31');
          }
        } else {
          // Invalid partial format
          errors.push('*Date must be in MM/DD/YYYY format');
        }
        return { isValid: false, errors, warnings };
      }

      // Parse date components
      const [month, day, year] = value.split('/').map(n => parseInt(n));
      
      // Validate components
      if (month < 1 || month > 12) {
        errors.push('*Month isn\'t right');
      }
      
      if (day < 1 || day > 31) {
        errors.push('*Day must be between 1 and 31');
      }

      const currentYear = new Date().getFullYear();
      
      // Date of birth specific validation - do this EARLY so it catches invalid years
      if (context?.fieldType === FieldType.DATE_OF_BIRTH) {
        // Year validation for DOB
        if (year < 1925) {
          errors.push('*Birth year must be 1925 or later');
        }
        if (year > currentYear) {
          errors.push('*Birth year cannot be in the future');
        }
        
        const age = currentYear - year;
        if (age < 18) {
          errors.push('*You must be at least 18 years old');
        }
        if (age > 100) {
          warnings.push('Please verify birth year for senior applicants');
        }
      }
      
      // General year validation (fallback)
      if (params?.minYear && year < params.minYear) {
        errors.push(`*Year cannot be earlier than ${params.minYear}`);
      }
      
      if (params?.maxYear && year > params.maxYear) {
        errors.push(`*Year cannot be later than ${params.maxYear}`);
      }

      // Advanced date validation
      if (errors.length === 0) {
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || 
            date.getMonth() !== month - 1 || 
            date.getDate() !== day) {
          errors.push('Please enter a valid date');
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    });

    // Phone validation
    this.registerRule('phone', (value) => {
      const digits = value.replace(/\D/g, '');
      const errors: string[] = [];
      const warnings: string[] = [];
      const phoneUtil = PhoneNumberUtil.getInstance();

      if (digits.length !== 10) {
        errors.push('*Phone number must be 10 digits');
      } else {
        // Strict validation using google-libphonenumber
        try {
          // Parse number with US default region
          const number = phoneUtil.parseAndKeepRawInput(digits, 'US');
          
          // Check if valid
          if (!phoneUtil.isValidNumber(number)) {
            // Get more specific reason if possible, or just generic invalid
            errors.push('*Please enter a valid US phone number');
          } else {
            // Additional check for mobile vs fixed line if needed, but isValidNumber covers most
            // We can also double check specifically for US validity to be safe
            if (!phoneUtil.isValidNumberForRegion(number, 'US')) {
               errors.push('*Please enter a valid US phone number');
            }
          }
        } catch (e) {
          // Fallback to basic validation if libphonenumber fails (e.g. invalid format)
          // US phone number validation fallback
          const area = digits.substring(0, 3);
          const exchange = digits.substring(3, 6);

          if (area.startsWith('0') || area.startsWith('1')) {
            errors.push('Area code cannot start with 0 or 1');
          }
          
          if (exchange.startsWith('0') || exchange.startsWith('1')) {
            errors.push('Exchange code cannot start with 0 or 1');
          }

          // Check for obviously invalid patterns
          if (/^(\d)\1{9}$/.test(digits)) {
            warnings.push('Phone number appears to be invalid');
          }
          
          // If we caught an exception but passed basic checks, we might still want to flag it
          // But usually parse exception means it's really bad
          if (errors.length === 0) {
             errors.push('*Invalid phone number format');
          }
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    });

    // ZIP code validation
    this.registerRule('zip', (value) => {
      const digits = value.replace(/\D/g, '');
      const errors: string[] = [];
      const warnings: string[] = [];

      if (digits.length !== 5 && digits.length !== 9) {
        errors.push('ZIP code must be 5 or 9 digits');
      }

      // Check for invalid patterns
      const invalidZips = ['00000', '11111', '22222', '33333', '44444', 
                          '55555', '66666', '77777', '88888', '99999'];
      
      if (digits.length === 5 && invalidZips.includes(digits)) {
        warnings.push('ZIP code appears to be invalid');
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    });

    // SSN validation (industry-standard format + basic validation)
    this.registerRule('ssn', (value) => {
      const raw = (value ?? '').trim();
      const digits = raw.replace(/\D/g, '');
      const errors: string[] = [];
      const warnings: string[] = [];

      // If the value carries any separators, they MUST form the canonical
      // XXX-XX-XXXX grouping. Stripping dashes first hid malformed groupings:
      // e.g. "122-544-848" (grouped 3-3-3) strips to 9 valid digits and used
      // to pass. Raw 9 digits with no separators are still fine — the SSN
      // formatter groups those into XXX-XX-XXXX.
      const hasSeparators = /\D/.test(raw);
      const isCanonical = /^\d{3}-\d{2}-\d{4}$/.test(raw);

      if (digits.length !== 9) {
        errors.push('*SSN must be 9 digits');
      } else if (hasSeparators && !isCanonical) {
        errors.push('*SSN format should be XXX-XX-XXXX');
      } else {
        // Industry-standard SSN validation rules
        // Note: Enhanced validation with Experian rules can be implemented here
        
        // Invalid SSN patterns
        if (digits === '000000000' || 
            digits.substring(0, 3) === '000' ||
            digits.substring(0, 3) === '666' ||
            digits.substring(3, 5) === '00' ||
            digits.substring(5, 9) === '0000') {
          errors.push('*Invalid SSN format');
        }

        // Area number validation (001-665, 667-899)
        const area = parseInt(digits.substring(0, 3));
        if (area >= 900 && area <= 999) {
          errors.push('*Invalid SSN area number');
        }

        // Check for obviously invalid patterns (all same digit)
        if (/^(\d)\1{8}$/.test(digits)) {
          errors.push('Invalid SSN pattern');
        }

        // Check for specific invalid 6-digit patterns if entered as 9 digits (e.g. repeated sequence)
        // or other obvious test patterns not caught by above
        if (digits === '123456789' || digits === '987654321') {
           warnings.push('SSN appears to be invalid');
        }

        // Additional industry-standard checks can be added here
        // TODO: Implement Experian/credit bureau specific validation rules
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    });

    // Currency/income validation (whole numbers only)
    this.registerRule('currency', (value, params, context) => {
      // Remove formatting characters but preserve the number
      const cleanValue = value.replace(/[$,\s]/g, '');
      const numValue = parseInt(cleanValue, 10);
      const errors: string[] = [];
      const warnings: string[] = [];

      if (isNaN(numValue) || cleanValue === '') {
        errors.push('*Please enter a valid amount');
        return { isValid: false, errors, warnings };
      }

      // Check for decimal values (not allowed)
      if (cleanValue.includes('.')) {
        errors.push('*Please enter whole numbers only (no decimals)');
      }

      if (numValue < 0) {
        errors.push('*Amount cannot be negative');
      }

      // Context-specific validation
      if (context?.fieldType === FieldType.CURRENCY) {
        const fieldName = context?.fieldName || '';
        
        // Housing payment validation
        if (fieldName.includes('housing_payment') || fieldName.includes('housing-payment')) {
          if (numValue === 0) {
            errors.push('*Housing payment must be greater than 0');
          }
          if (numValue > 0 && numValue < 200) {
            warnings.push('*Are you sure? This housing payment amount seems very low.');
          }
        }
        
        // Income validation  
        if (fieldName.includes('income')) {
          if (numValue === 0) {
            errors.push('*Income must be greater than 0');
          }
          if (numValue > 0 && numValue < 1000) {
            warnings.push('*Are you sure? This income amount seems very low.');
          }
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    });

    // Email validation (RFC 5322 compliant)
    this.registerRule('email', (value) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      if (!value || value.trim() === '') {
        errors.push('*Email address is required');
        return { isValid: false, errors, warnings };
      }

      // RFC 5322 compliant email regex (requires TLD)
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
      
      if (!emailRegex.test(value)) {
        errors.push('*Please enter a valid email address');
      }

      // Additional basic checks
      if (value.length > 254) {
        errors.push('*Email address is too long');
      }

      // Enhanced domain correction system
      const lowerValue = value.toLowerCase();
      const domainCorrections = this.checkForDomainCorrections(lowerValue);
      
      if (domainCorrections.hasSuggestion) {
        warnings.push(domainCorrections.message);
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings
      };
    });

    // Future-date validation (used by next-pay-date on the additional-info form).
    // Accepts MM/DD/YYYY input, requires the date to be a real calendar date AND in the future
    // (i.e. today or later). Optional `maxDaysAhead` clamps how far out the date can be.
    this.registerRule('future_date', (value, params) => {
      const errors: string[] = [];

      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        if (value.length > 0) errors.push('*Date must be in MM/DD/YYYY format');
        return { isValid: errors.length === 0, errors, warnings: [] };
      }

      const [month, day, year] = value.split('/').map((n) => parseInt(n, 10));

      if (month < 1 || month > 12) errors.push('*Month isn\'t right');
      if (day < 1 || day > 31) errors.push('*Day must be between 1 and 31');

      if (errors.length === 0) {
        const date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
          errors.push('*Please enter a valid date');
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const target = new Date(year, month - 1, day);
          target.setHours(0, 0, 0, 0);

          if (target < today) {
            errors.push('*Date must be today or later');
          } else if (params?.maxDaysAhead) {
            const maxDate = new Date(today);
            maxDate.setDate(maxDate.getDate() + params.maxDaysAhead);
            if (target > maxDate) {
              errors.push(`*Date must be within ${params.maxDaysAhead} days from today`);
            }
          }
        }
      }

      return { isValid: errors.length === 0, errors, warnings: [] };
    });

    // Name validation (No spaces allowed)
    this.registerRule('name', (value) => {
      const errors: string[] = [];
      
      if (value.trim().includes(' ')) {
        errors.push('Spaces are not allowed in this field');
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings: []
      };
    });
  }

  /**
   * Check for common email domain typos and provide correction suggestions
   */
  private checkForDomainCorrections(email: string): { hasSuggestion: boolean; message: string } {
    // Extract domain part
    const atIndex = email.lastIndexOf('@');
    if (atIndex === -1) return { hasSuggestion: false, message: '' };
    
    const domain = email.substring(atIndex + 1);
    
    // Common domain corrections mapping
    const domainCorrections: { [key: string]: string } = {
      // Gmail variations
      'gmail.co': 'gmail.com',
      'gmail.om': 'gmail.com', 
      'gmai.com': 'gmail.com',
      'gmial.com': 'gmail.com',
      'gmaill.com': 'gmail.com',
      'gmal.com': 'gmail.com',
      'gmil.com': 'gmail.com',
      'gmail.con': 'gmail.com',
      'gamil.com': 'gmail.com',
      
      // Yahoo variations
      'yahoo.co': 'yahoo.com',
      'yahoo.om': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'yahooo.com': 'yahoo.com',
      'yahoo.con': 'yahoo.com',
      'yahoo.cm': 'yahoo.com',
      
      // Hotmail/Outlook variations  
      'hotmail.co': 'hotmail.com',
      'hotmail.om': 'hotmail.com',
      'hotmal.com': 'hotmail.com',
      'hotmil.com': 'hotmail.com',
      'hotmail.con': 'hotmail.com',
      'outlook.co': 'outlook.com',
      'outlook.om': 'outlook.com',
      'outlok.com': 'outlook.com',
      
      // Other common providers
      'icloud.co': 'icloud.com',
      'icloud.om': 'icloud.com',
      'aol.co': 'aol.com',
      'aol.om': 'aol.com',
      
      // Generic TLD corrections
      '.co': '.com',
      '.om': '.com',
      '.con': '.com',
      '.cm': '.com'
    };

    // Check for exact domain matches
    for (const [typo, correction] of Object.entries(domainCorrections)) {
      if (domain === typo) {
        const correctedEmail = email.substring(0, atIndex + 1) + correction;
        return {
          hasSuggestion: true,
          message: `Did you mean ${correctedEmail}?`
        };
      }
    }

    // Check for domain endings
    for (const [typoEnding, correctionEnding] of Object.entries(domainCorrections)) {
      if (typoEnding.startsWith('.') && domain.endsWith(typoEnding)) {
        const baseDomain = domain.substring(0, domain.length - typoEnding.length);
        const correctedDomain = baseDomain + correctionEnding;
        const correctedEmail = email.substring(0, atIndex + 1) + correctedDomain;
        return {
          hasSuggestion: true,
          message: `Did you mean ${correctedEmail}?`
        };
      }
    }

    return { hasSuggestion: false, message: '' };
  }
}

/**
 * Global validation rule registry
 */
let globalValidationRegistry: ValidationRuleRegistry | null = null;

/**
 * Get the global validation rule registry
 */
export function getValidationRegistry(): ValidationRuleRegistry {
  if (!globalValidationRegistry) {
    globalValidationRegistry = new ValidationRuleRegistry();
  }
  return globalValidationRegistry;
}

/**
 * Format-specific validation utilities
 */
export class FormatValidators {
  private static registry = getValidationRegistry();

  /**
   * Validate phone number format
   */
  public static validatePhone(value: string): ValidationResult {
    return FormatValidators.registry.processRule('phone', value);
  }

  /**
   * Validate ZIP code format
   */
  public static validateZip(value: string): ValidationResult {
    return FormatValidators.registry.processRule('zip', value);
  }

  /**
   * Validate date format
   */
  public static validateDate(value: string, fieldType?: FieldType): ValidationResult {
    return FormatValidators.registry.processRule('date', value, {}, {
      fieldType: fieldType || FieldType.DATE_OF_BIRTH,
      value,
      rawValue: value.replace(/\D/g, '')
    });
  }

  /**
   * Validate currency format
   */
  public static validateCurrency(value: string, fieldContext?: string): ValidationResult {
    return FormatValidators.registry.processRule('currency', value, 
      { fieldContext }, 
      {
        fieldType: FieldType.CURRENCY,
        value,
        rawValue: value.replace(/[$,]/g, '')
      }
    );
  }

  /**
   * Validate SSN format
   */
  public static validateSSN(value: string): ValidationResult {
    return FormatValidators.registry.processRule('ssn', value);
  }

  /**
   * Validate email format (RFC 5322 compliant)
   */
  public static validateEmail(value: string): ValidationResult {
    return FormatValidators.registry.processRule('email', value);
  }

  /**
   * Validate field based on field type
   */
  public static validateByFieldType(
    fieldType: FieldType, 
    value: string, 
    options?: Record<string, any>,
    context?: ValidationContext
  ): ValidationResult {
    switch (fieldType) {
      case FieldType.PHONE:
        return FormatValidators.validatePhone(value);
      case FieldType.ZIP:
        return FormatValidators.validateZip(value);
      case FieldType.DATE_OF_BIRTH:
        return FormatValidators.registry.processRule('date', value, { fieldType }, context);
      case FieldType.CURRENCY:
        return FormatValidators.registry.processRule('currency', value, options, context);
      case FieldType.SSN:
        return FormatValidators.validateSSN(value);
      case FieldType.NAME:
        return FormatValidators.registry.processRule('name', value);
      default:
        return {
          isValid: true,
          errors: [],
          warnings: []
        };
    }
  }

  /**
   * Validate using custom rules
   */
  public static validateWithRules(
    value: string,
    rules: ValidationRule[],
    context?: ValidationContext
  ): ValidationResult {
    const allErrors: string[] = [];
    const allWarnings: string[] = [];

    for (const rule of rules) {
      const result = FormatValidators.registry.processRule(
        rule.type,
        value,
        rule.params,
        context
      );

      allErrors.push(...result.errors);
      allWarnings.push(...(result.warnings || []));
    }

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings
    };
  }

  /**
   * Check if value is empty or just whitespace
   */
  public static isEmpty(value: string): boolean {
    return !value || value.trim().length === 0;
  }

  /**
   * Check if value contains only allowed characters for field type
   */
  public static hasValidCharacters(value: string, fieldType: FieldType): boolean {
    switch (fieldType) {
      case FieldType.PHONE:
        return /^[\d\-\(\)\s]*$/.test(value);
      case FieldType.ZIP:
        return /^[\d\-]*$/.test(value);
      case FieldType.DATE_OF_BIRTH:
        return /^[\d\/]*$/.test(value);
      case FieldType.CURRENCY:
        return /^[\d\.,\$\s]*$/.test(value);
      case FieldType.SSN:
        return /^[\dX\-]*$/.test(value); // Allow X for masked values
      default:
        return true;
    }
  }

  /**
   * Get field type specific error messages
   */
  public static getFieldTypeErrorMessages(fieldType: FieldType): Record<string, string> {
    const messages: Record<FieldType, Record<string, string>> = {
      [FieldType.PHONE]: {
        required: 'Phone number is required',
        invalid: 'Please enter a valid 10-digit phone number',
        format: 'Phone number format should be XXX-XXX-XXXX'
      },
      [FieldType.ZIP]: {
        required: 'ZIP code is required',
        invalid: 'Please enter a valid 5-digit ZIP code',
        format: 'ZIP code format should be XXXXX'
      },
      [FieldType.DATE_OF_BIRTH]: {
        required: 'Date of birth is required',
        invalid: 'Please enter a valid date',
        format: 'Date format should be MM/DD/YYYY'
      },
      [FieldType.CURRENCY]: {
        required: 'Amount is required',
        invalid: 'Please enter a valid whole number amount',
        format: 'Amount should be a whole number (no decimals)'
      },
      [FieldType.SSN]: {
        required: 'SSN is required',
        invalid: 'Please enter a valid 9-digit SSN',
        format: 'SSN format should be XXX-XX-XXXX'
      },
      [FieldType.NAME]: {
        required: 'This field is required',
        invalid: 'Spaces are not allowed in this field',
        format: 'Please enter a valid name without spaces'
      },
      [FieldType.UNKNOWN]: {
        required: 'This field is required',
        invalid: 'Please enter a valid value',
        format: 'Invalid format'
      }
    };

    return messages[fieldType] || messages[FieldType.UNKNOWN];
  }
}